<template>
  <div class="card-list-data">
    <div class="course-picker">
      <div class="card-light-bg">
        <h5 class="picker-subhead">{{ $t('quick_booking.lbl_choose_date') }}</h5>
        <flat-pickr v-model="date" :config="config" @change="dateUpdate" />
      </div>
      <div class="card-light-bg iq-time-slot p-3">
        <h5 class="picker-subhead">{{ $t('quick_booking.lbl_available_times') }}</h5>
        <!-- Loading State -->
        <div v-if="IS_LOADER" class="text-center">
          <div class="iq-time-animation d-grid grid-template-3 gap-3">
            <label v-for="index in 12" :key="index" class="skeleton skeleton-label-box"></label>
          </div>
          <p class="mt-3 text-primary">
            <i class="fas fa-spinner fa-spin me-2"></i>{{ $t('quick_booking.loading_slots') }}
          </p>
        </div>

        <!-- Loaded State -->
        <template v-else>
          <div class="iq-time-animation d-grid grid-template-3 gap-3" v-if="filteredTimeSlotList.length > 0">
            <template v-for="(item, index) in filteredTimeSlotList" :key="`items-${index}`">
              <div>
                <input type="radio" :id="item.value" v-model="start_date_time" :value="item.value" name="radio" class="btn-check" @change="onChange" />
                <!-- No d-block/py-2/px-2: those are !important utilities and
                     `display: block` beat the stylesheet's flex centering, which
                     left the hour pinned to the top of the button. The button
                     sizes and centres itself in the stylesheet instead. -->
                <label :for="item.value" class="btn">{{ slotLabel(item) }}</label>
              </div>
            </template>
          </div>
          
          <!-- Empty/Prerequisite State -->
          <div class="d-flex align-items-center justify-content-center flex-grow-1 py-4 text-center" v-else>
            <p class="mb-0">
              <template v-if="!date">{{ $t('quick_booking.please_select_date') }}</template>
              <template v-else>{{ $t('quick_booking.no_slots_available') }}</template>
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
  <!-- Back must stay reachable even when the chosen day has no free slots -->
  <div class="card-footer">
    <button type="button" class="btn btn-secondary iq-text-uppercase" v-if="wizardPrev" @click="prevTabChange(wizardPrev)">{{ $t('quick_booking.lbl_back') }}</button>
    <button type="button" v-if="timeSlotList.length > 0 && wizardNext" class="btn btn-primary iq-text-uppercase" :disabled="!start_date_time" @click="nextTabChange(wizardNext)">{{ $t('quick_booking.lbl_next') }}</button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRequest } from '@/helpers/hooks/useCrudOpration'
import { useQuickBooking } from '../../store/quick-booking'
import { SLOT_TIME_LIST, HOLIDAY_SLOT_LIST } from '@/vue/constants/quick_booking'
import flatPickr from 'vue-flatpickr-component'
// flatpickr ships its own month/weekday names and defaults to English, so the
// calendar stayed English while the rest of the page was translated.
import { Bulgarian } from 'flatpickr/dist/l10n/bg.js'
import * as moment from 'moment'

const props = defineProps({
  wizardNext: { default: '', type: [String, Number] },
  wizardPrev: { default: '', type: [String, Number] }
})

const emit = defineEmits(['tab-change'])

const date = ref(null)
const start_date_time = ref(null)
const IS_LOADER = ref(true)
const timeSlotList = ref([])
const holidaySlotList = ref([])
const holidaydate = ref([])
const isHoliday = ref(false)

const store = useQuickBooking()
const { listingRequest } = useRequest()

// Follow whatever locale the server rendered; any other language keeps the
// flatpickr default rather than being forced into Bulgarian.
const pageLocale = (document.documentElement.lang || 'en').toLowerCase()

const config = ref({
  inline: true,
  ...(pageLocale.startsWith('bg') ? { locale: Bulgarian } : {}),
  dateFormat: 'Y-m-d',
  minDate: 'today',
  maxDate: moment().add(6, 'months').format('YYYY-MM-DD'),
  disable: []
})

/* The API labels every slot as `h:i A`, which reads wrong in Bulgarian — 24h is
   the local convention. Reformat from the raw `value` here rather than in
   BookingTrait::getQuickSlots, which the admin booking screen shares. */
const slotLabel = (item) => (pageLocale.startsWith('bg') ? moment(item.value).format('HH:mm') : item.label)

const filteredTimeSlotList = computed(() => {
  const currentTime = moment()
  return timeSlotList.value.filter((slot) => moment(slot.value).isAfter(currentTime))
})

const serviceIdsKey = computed(() =>
  (store.booking.services || [])
    .map((service) => service.service_id)
    .filter((id) => id !== null && id !== undefined)
    .join(',')
)

const getSlots = () => {
  const branch_id = store.booking.branch_id
  const selectedDate = date.value
  const service_id = serviceIdsKey.value

  if (!branch_id || !selectedDate || !service_id) {
    return
  }

  // Prevent double calls if already loading
  if (IS_LOADER.value) return

  IS_LOADER.value = true
  timeSlotList.value = [] // Clear previous slots to avoid "No result" flash or stale data

  listingRequest({
    url: SLOT_TIME_LIST,
    data: { 
      branch_id, 
      date: selectedDate, 
      employee_id: store.booking.employee_id || null, 
      service_id 
    }
  }).then((res) => {
    IS_LOADER.value = false
    timeSlotList.value = res.data || []
    
    // Auto-update employee if backend suggests one (e.g. for "no preference" mapping)
    const responseEmployeeId = res?.employee_id
    if (responseEmployeeId && responseEmployeeId !== store.booking.employee_id) {
      store.updateBookingEmployeeValues(responseEmployeeId)
      store.updateBookingValues({ key: 'employee_id', value: responseEmployeeId })
    }
  }).catch((err) => {
    console.error('Failed to fetch slots:', err)
    IS_LOADER.value = false
    timeSlotList.value = []
  })
}
// console.log(store.booking);

const canFetchHolidaySlots = () => {
  return !!(store.booking.branch_id && serviceIdsKey.value && store.booking.employee_selection_token)
}

const getHolidaySlots = () => {
  if (!canFetchHolidaySlots()) {
    return
  }
  IS_LOADER.value = true
  console.log(store.booking.employee_id)

  listingRequest({
    url: HOLIDAY_SLOT_LIST,
    data: {
      branch_id: store.booking.branch_id,
      employee_id: store.booking.employee_id ?? '',
      service_id: serviceIdsKey.value
    }
  }).then((res) => {
    setTimeout(() => {
      IS_LOADER.value = false
      holidaySlotList.value = res.data
      holidaydate.value = res.holidays
      updateHolidayConfig()
    }, 1000)
  })
}

const updateHolidayConfig = () => {
  const disabledDays = holidaySlotList.value.filter((slot) => slot.is_holiday === 1).map((slot) => slot.day.toLowerCase())

  const holidayDates = holidaydate.value // Get specific holiday dates
  console.log(store.booking.employee_id, store.booking.employee_id != null || store.booking.employee_id != 0 || store.booking.employee_id != undefined)

  if (store.booking.employee_id != null && store.booking.employee_id != 0 && store.booking.employee_id != undefined) {
    config.value.disable = getDisabledDays([], holidayDates)
  } else {
    config.value.disable = getDisabledDays(disabledDays, holidayDates)
  }
}
const getDisabledDays = (days, holidayDates) => {
  // console.log(days , holidayDates);

  const disabledDates = new Set(holidayDates)
  const currentDate = moment()
  for (let i = 0; i < 365; i++) {
    const date = currentDate.clone().add(i, 'days')
    const dayOfWeek = date.format('dddd').toLowerCase()
    if (days != [] && days.includes(dayOfWeek)) {
      disabledDates.add(date.format('YYYY-MM-DD'))
    }
  }
  return Array.from(disabledDates)
}

const dateUpdate = () => getSlots()

onMounted(() => {
  getHolidaySlots()
})

watch(
  () => store.booking.employee_selection_token,
  (token) => {
    if (token) {
      getHolidaySlots()
      if (date.value) {
        getSlots()
      }
    }
  }
)

watch(
  serviceIdsKey,
  (serviceId) => {
    if (serviceId) {
      getHolidaySlots()
    }
  }
)

watch(
  () => store.booking.branch_id,
  (branchId) => {
    if (branchId) {
      getHolidaySlots()
    }
  }
)

watch(
  () => start_date_time.value,
  (value) => {
    store.updateBookingValues({ key: 'start_date_time', value })
    store.updateBookingServiceTimeValues(value)
  }
)

const onChange = () => emit('tab-change', props.wizardNext)

const nextTabChange = (val) => emit('tab-change', val)
const prevTabChange = (val) => {
  resetData()
  emit('tab-change', val)
}

const resetData = () => {
  store.updateBookingValues({ key: 'start_date_time', value: null })
  store.updateBookingServiceTimeValues(null)
  start_date_time.value = null
  date.value = null
  timeSlotList.value = []
  store.updateBookingValues({ key: 'employee_selection_token', value: null })
}
</script>

<style scoped>
.list-group {
  border-radius: 0;
  gap: 1.5rem;
}

.list-group-item {
  border: var(--bs-list-group-border-width) solid var(--bs-list-group-border-color) !important;
  border-radius: var(--bs-border-radius);
}

.iq-time-animation {
  align-content: start;
}
</style>
